"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";
import {
  authSignup,
  authSignupEmailCheck,
  authSignupPhoneCheck,
} from "@/entities";
import { useState } from "react";

export function SignupPage() {
  const router = useRouter();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    setError,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      password: "",
      passwordConfirm: "",
      agreement1: false,
    },
  });

  const [emailChecker, setEmailChecker] = useState(false);
  const emailCheckMutation = useMutation({
    mutationFn: authSignupEmailCheck,
    onSuccess: ({ ok, error }: { ok: boolean; error?: string }) => {
      if (ok) {
        setEmailChecker(true);
      } else {
        setEmailChecker(false);
        setError("email", {
          type: "error",
          message: error,
        });
      }
    },
    onError: (e: any) => {
      toast.error(`에러(${e.message})가 발생했습니다. 다시 시도해 주세요.`);
    },
  });
  const onEmailCheck = async (email: string) => {
    const noEngRule =
      /^[0-9a-zA-Z]([-_\.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_\.]?[0-9a-zA-Z])*\.[a-zA-Z]{2,3}$/;
    if (!noEngRule.test(email)) {
      setError("email", {
        type: "error",
        message: "아이디를 이메일 형식으로 입력해주세요.",
      });
      setEmailChecker(false);
      return;
    }

    if (emailCheckMutation?.isPending) return;
    await emailCheckMutation.mutateAsync(email);
  };

  const [phoneChecker, setPhoneChecker] = useState(false);
  const phoneCheckMutation = useMutation({
    mutationFn: authSignupPhoneCheck,
    onSuccess: ({ ok, error }: { ok: boolean; error?: string }) => {
      if (ok) {
        setPhoneChecker(true);
      } else {
        setPhoneChecker(false);
        setError("phone", {
          type: "error",
          message: error,
        });
      }
    },
    onError: (e: any) => {
      toast.error(`에러(${e.message})가 발생했습니다. 다시 시도해 주세요.`);
    },
  });
  const onPhoneCheck = async (phone: string) => {
    const phoneRegex = /^01([0|1|6|7|8|9])-?([0-9]{4})-?([0-9]{4})$/;
    if (!phoneRegex.test(phone)) {
      setError("phone", {
        type: "error",
        message: "휴대폰번호는 10-11자 이내로 입력해주세요.",
      });
      setPhoneChecker(false);
      return;
    }

    if (phoneCheckMutation?.isPending) return;
    await phoneCheckMutation.mutateAsync(phone);
  };

  const signupMutation = useMutation({
    mutationFn: authSignup,
    onSuccess: () => {
      router.push("/admin/main");
    },
    onError: (e: any) => {
      toast.error(`에러(${e.message})가 발생했습니다. 다시 시도해 주세요.`);
    },
  });
  const onSubmit = async (data: any) => {
    if (signupMutation.isPending) return;

    onPhoneCheck(data.phone);
    if (!phoneChecker) {
      setError("phone", {
        type: "error",
        message: "휴대폰번호는 10-11자 이내로 입력해주세요.",
      });
      return;
    }

    onEmailCheck(data.email);
    if (!emailChecker) {
      setError("email", {
        type: "error",
        message: "아이디를 이메일 형식으로 입력해주세요.",
      });
      return;
    }

    await signupMutation.mutateAsync(data);
  };

  return (
    <div className="login login-v1">
      <div className="login-container">
        <div className="login-header">
          <div className="brand">
            <div className="d-flex align-items-center">회원가입</div>
            <small>새 계정을 만드세요</small>
          </div>
          <div className="icon">
            <i className="fa fa-user"></i>
          </div>
        </div>
        <div className="login-body">
          <div className="login-content fs-13px">
            <form onSubmit={handleSubmit(onSubmit)} className="fs-13px">
              <Controller
                name="name"
                control={control}
                rules={{
                  required: "닉네임 입력해주세요.",
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <div className="mb-3">
                      <label className="mb-2">
                        닉네임 <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        title="닉네임을 입력해주세요."
                        placeholder="닉네임을 입력해주세요."
                        className="form-control fs-13px"
                        onChange={onChange}
                        onBlur={onBlur}
                        value={value || ""}
                      />
                      {errors.name && (
                        <span className="text-red errorText">
                          {errors.name.message as string}
                        </span>
                      )}
                    </div>
                  </>
                )}
              />

              <Controller
                name="phone"
                control={control}
                rules={{
                  required: "휴대폰번호는 10-11자 이내로 입력해주세요.",
                  minLength: {
                    value: 10,
                    message: "휴대폰번호는 10-11자 이내로 입력해주세요.",
                  },
                  maxLength: {
                    value: 11,
                    message: "휴대폰번호는 10-11자 이내로 입력해주세요.",
                  },
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <div className="mb-3">
                      <label className="mb-2">
                        휴대전화 번호 <span className="text-danger">*</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        title="휴대전화 번호를 입력해주세요."
                        placeholder="휴대전화 번호를 입력해주세요."
                        maxLength={11}
                        className="form-control fs-13px"
                        onChange={(event) => {
                          const value = event.target.value.replace(/-/g, "");
                          onChange(value);
                          onPhoneCheck(value);
                        }}
                        onBlur={onBlur}
                        value={value || ""}
                      />
                      {errors.phone && (
                        <span className="text-red errorText">
                          {errors.phone.message as string}
                        </span>
                      )}
                    </div>
                  </>
                )}
              />

              <Controller
                name="email"
                control={control}
                rules={{
                  validate: (value) => {
                    const noEngRule =
                      /^[0-9a-zA-Z]([-_\.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_\.]?[0-9a-zA-Z])*\.[a-zA-Z]{2,3}$/;
                    return !noEngRule.test(value)
                      ? "이메일 형식으로 입력해주세요."
                      : undefined;
                  },
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <div className="mb-3">
                      <label className="mb-2" htmlFor="email">
                        아이디(이메일) <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        title="아이디(이메일)를 입력해주세요."
                        placeholder="아이디(이메일)를 입력해주세요."
                        className="form-control fs-13px"
                        onChange={(event) => {
                          const value = event.target.value.toLowerCase();
                          onChange(value);
                          onEmailCheck(value);
                        }}
                        onBlur={onBlur}
                        value={value || ""}
                      />
                      {errors.email && (
                        <span className="text-red errorText">
                          {errors.email.message as string}
                        </span>
                      )}
                    </div>
                  </>
                )}
              />

              <Controller
                name="password"
                control={control}
                rules={{
                  required: "비밀번호를 입력해주세요.",
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <div className="mb-3">
                      <label className="mb-2">
                        비밀번호 <span className="text-danger">*</span>
                      </label>
                      <input
                        type="password"
                        id="password"
                        title="비밀번호를 입력해주세요."
                        placeholder="비밀번호를 입력해주세요."
                        className="form-control fs-13px"
                        onChange={onChange}
                        onBlur={onBlur}
                        value={value || ""}
                      />
                      {errors.password && (
                        <span className="text-red errorText">
                          {errors.password.message as string}
                        </span>
                      )}
                    </div>
                  </>
                )}
              />

              <Controller
                name="passwordConfirm"
                control={control}
                rules={{
                  required: "비밀번호 재입력을 입력해주세요.",
                  validate: (value) => {
                    return value !== watch("password")
                      ? "비밀번호와 비밀번호 재입력이 일치하지 않습니다."
                      : undefined;
                  },
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <div className="mb-3">
                      <label className="mb-2" htmlFor="passwordConfirm">
                        비밀번호 재입력 <span className="text-danger">*</span>
                      </label>
                      <input
                        type="password"
                        id="passwordConfirm"
                        title="비밀번호 재입력을 입력해주세요."
                        placeholder="비밀번호 재입력을 입력해주세요."
                        className="form-control fs-13px"
                        onChange={onChange}
                        onBlur={onBlur}
                        value={value || ""}
                      />
                      {errors.passwordConfirm && (
                        <span className="text-red errorText">
                          {errors.passwordConfirm.message as string}
                        </span>
                      )}
                    </div>
                  </>
                )}
              />

              <Controller
                name="agreement1"
                control={control}
                rules={{
                  required: "이용약관에 동의해주세요.",
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <div className="form-check mb-4">
                      <input
                        type="checkbox"
                        id="agreementCheckbox"
                        className="form-check-input"
                        value="Y"
                        onChange={onChange}
                        onBlur={onBlur}
                        checked={value}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="agreementCheckbox"
                      >
                        이용약관에 동의합니다.
                      </label>
                      {errors.agreement1 && (
                        <div>
                          <span className="text-red errorText">
                            {errors.agreement1.message as string}
                          </span>
                        </div>
                      )}
                    </div>
                  </>
                )}
              />

              <div className="mb-4">
                <button
                  type="submit"
                  className="btn btn-theme d-block w-100 btn-lg h-45px fs-13px"
                  disabled={signupMutation.isPending}
                >
                  확인
                </button>
              </div>
              <div>
                이미 회원이신가요?{" "}
                <Link
                  href="/auth/login"
                  className="text-white text-decoration-none fw-bold"
                >
                  여기
                </Link>
                를 클릭하여 로그인하세요.
              </div>
            </form>
          </div>
        </div>
        <div id="footer" className="app-footer text-center">
          &copy; 2025 wezoncoop. All Right Reserved
        </div>
      </div>
    </div>
  );
}
